import { TableCellType } from "components/LazyTable";
import SortableTable from "components/SortableTable";
import { withErrorBoundary } from "hocs/withErrorBoundary";
import useGlobalContext from "hooks/globalData";
import { useStrictQuery } from "hooks/useStrictQuery";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import {
  ListRequestOfBaseFilter,
  ListResponseOfSkuResource,
  SkuResource,
} from "services/KnowfullyAdminClient";
import { SkuResourceWithId } from "types/sku-related-types";
import SkuResourceActionButtons from "./SkuResourceActionButtons";

const SkuResources = ({ skuId }: { skuId: string }) => {
  const { formatMessage: t } = useIntl();
  const navigate = useNavigate();

  const { skuService, resourceDetailsUrl, skuDetailsUrl } = useGlobalContext();

  const { data, isFetching } = useStrictQuery<ListResponseOfSkuResource>(
    ["skuResources", skuId],
    () =>
      skuService.getSkuResources(
        skuId,
        ListRequestOfBaseFilter.fromJS({
          sortField: "sequence",
          sortDescending: false,
        })
      ),
    { enabled: !!skuId }
  );

  const SkuResourceActions = ({ value }: { value: SkuResourceWithId }) => {
    const skuResource = {
      isSkuUserAgreementAccepted: value.isSkuUserAgreementAccepted,
      isSkuUserAgreementPending: value.isSkuUserAgreementPending,
      hasOrganizationUserId: value.hasOrganizationUserId,
      organizationUserId: value.resource?.organizationUserId,
      skuId: skuId,
    };

    return <SkuResourceActionButtons {...skuResource} />;
  };

  const cells: Array<TableCellType> = [
    {
      accessor: "resource.value",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "skuFacultyMemberLabel" }),
    },
    {
      accessor: "resourceType.value",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "skuFacultyTypeLabel" }),
    },
    {
      accessor: "value",
      align: "center",
      width: 1,
      type: "node",
      View: SkuResourceActions,
      label: t({ id: "agreements" }),
      noSort: true,
    },
  ];

  const onResourceRowClick = (_: string, row: SkuResource) => {
    navigate(resourceDetailsUrl(row.resource!.id), {
      state: { from: skuDetailsUrl(row.skuId) },
    });
  };

  return (
    <SortableTable
      {...{
        rows: data?.data! as SkuResourceWithId[],
        loading: isFetching,
        dense: true,
        cells,
        tableRowPerPage: 5,
        defaultOrderBy: "sequence",
        onClick: onResourceRowClick,
      }}
    />
  );
};

export default withErrorBoundary(SkuResources);
